import React, { useState, useEffect, useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
//import 'pages/Manager.css';
import ModalDialog from './ModalDialog';
import '@esri/calcite-components/dist/components/calcite-accordion';
import '@esri/calcite-components/dist/components/calcite-accordion-item';
import '@esri/calcite-components/dist/components/calcite-block';
import '@esri/calcite-components/dist/components/calcite-button';
import '@esri/calcite-components/dist/components/calcite-flow';
import '@esri/calcite-components/dist/components/calcite-flow-item';
import '@esri/calcite-components/dist/components/calcite-input';
import '@esri/calcite-components/dist/components/calcite-label';
import '@esri/calcite-components/dist/components/calcite-list';
import '@esri/calcite-components/dist/components/calcite-list-item';
import '@esri/calcite-components/dist/components/calcite-panel';
import '@esri/calcite-components/dist/components/calcite-popover';
import '@esri/calcite-components/dist/components/calcite-switch';
import {
    CalciteAccordion,
    CalciteAccordionItem,
    CalciteBlock,
    CalciteButton,
    CalciteFlow,
    CalciteFlowItem,
    CalciteInput,
    CalciteLabel,
    CalciteList,
    CalciteListItem,
    CalcitePanel,
    CalcitePopover,
    CalciteSwitch
} from '@esri/calcite-components-react';

type ChooseFontProps = {
    title?: string;
    appHelp?: string;
    icon?: any;
    message?: any;
    large?: boolean;
    show?: boolean;
    params?: any;
    onClose?: () => void;
    fonts?: any[];
    showChartsOption?: boolean;
    onChange?: (font: string, cssSize: string) => void;
    onChoose?: (font: string, cssSize: string, applyToCharts?: boolean, customArgs?: any) => void;
    family?: string;
    size?: string;
    charts?: boolean;
    mode?: 'dialog' | 'panel';
    [id: string]: any;
};

const ChooseFontDialog = ({
    title,
    appHelp = '',
    icon,
    message,
    large,
    show,
    params,
    onClose,
    fonts,
    showChartsOption = false,
    onChange,
    onChoose,
    family: initialFontFamily = 'Lato',
    size: initialFontSize = '16px',
    charts: initialApplyToCharts = false,
    mode = 'dialog',
    ...others
}: ChooseFontProps) => {
    const [family, setFamily] = useState<string>(initialFontFamily),
        [size, setSize] = useState<number>(
            initialFontSize !== undefined
                ? parseInt(initialFontSize.toString().replace(/[^0-9\.]/g, ''))
                : 16
        ),
        [charts, setCharts] = useState<boolean>(initialApplyToCharts),
        [activeModal, setActiveModal] = useState<string | null>(null),
        fontBtn = useRef<any>(null);

    useEffect(() => {
        injectStandardFonts(fonts);
    }, [fonts]);

    useEffect(() => {
        if (onChange !== undefined && onChange !== null) onChange(family, `${size}px`);
    }, [onChange, family, size]);

    const onSelectionChange = (evt: any, fontFamily: string) => {
        setFamily(fontFamily);
    };

    const onFamilyChange = (evt: any) => {
        const itemValue = evt.currentTarget.value;
        setFamily(itemValue);
    };

    const onSizeChange = (evt: any) => {
        const itemValue = evt.currentTarget.value;
        setSize(parseInt(itemValue));
    };

    const onChartsChange = (evt: any) => {
        const itemValue = evt.currentTarget.checked;
        setCharts(itemValue);
    };

    const onChosenClick = () => {
        const customArgs = params !== undefined ? params.customArgs : undefined;
        if (onChoose !== undefined) onChoose(family, `${size}px`, charts, customArgs);
    };

    const items = fonts !== undefined ? fonts : STANDARD_FONTS,
        fontForm = (
            <div className="form-horizontal settings-holder pad-5">
                <div className="input-row">
                    <CalciteLabel>
                        <FormattedMessage
                            id="chooseFontDialog.label.family"
                            defaultMessage="Family"
                        />
                        <CalciteInput
                            type="text"
                            value={family}
                            onCalciteInputInput={onFamilyChange}
                            style={{
                                fontFamily: family
                            }}
                        >
                            <CalciteButton
                                id="rbChooseFontFamilyBtn"
                                onClick={() => {
                                    setActiveModal('chooseFamilyDialog');
                                }}
                                slot="action"
                                iconStart="ellipsis"
                                kind="neutral"
                                appearance="outline"
                                ref={fontBtn}
                            ></CalciteButton>
                        </CalciteInput>
                    </CalciteLabel>
                </div>
                {activeModal === 'chooseFamilyDialog' && (
                    <FontChooserPopover
                        referenceElement={fontBtn.current}
                        selected={family}
                        fonts={items}
                        onClose={() => {
                            setActiveModal(null);
                        }}
                        onSelectionChange={onSelectionChange}
                    />
                )}
                <div className="input-row">
                    <CalciteLabel>
                        <FormattedMessage id="chooseFontDialog.label.size" defaultMessage="Size" />
                        <CalciteInput
                            type="number"
                            step={1}
                            suffixText="px"
                            onCalciteInputChange={onSizeChange}
                            value={size.toFixed(0)}
                        ></CalciteInput>
                    </CalciteLabel>
                </div>
                {showChartsOption ? (
                    <div className="input-row">
                        <CalciteLabel>
                            <FormattedMessage
                                id="chooseFontDialog.label.charts"
                                defaultMessage="Force all charts to use this font?"
                            />
                            <CalciteSwitch
                                onCalciteSwitchChange={onChartsChange}
                                checked={charts === true ? true : undefined}
                            />
                        </CalciteLabel>
                    </div>
                ) : null}
            </div>
        ),
        primaryButton = (
            <CalciteButton
                iconStart="check"
                kind="brand"
                appearance="solid"
                slot={mode === 'panel' ? 'footer-actions' : 'footer-end'}
                onClick={(e) => onChosenClick()}
                disabled={
                    family === null ||
                    (family === initialFontFamily && `${size}px` === initialFontSize)
                        ? true
                        : undefined
                }
                width={mode === 'panel' ? 'half' : 'full'}
            >
                <FormattedMessage id="chooseFontDialog.button.choose" defaultMessage="Apply" />
            </CalciteButton>
        ),
        secondaryButton = (
            <CalciteButton
                iconStart="x"
                kind="brand"
                appearance="outline"
                slot={mode === 'panel' ? 'footer-actions' : 'footer-end'}
                onClick={(e) => {
                    if (onClose !== undefined) onClose();
                }}
                width={mode === 'panel' ? 'half' : 'full'}
            >
                <FormattedMessage id="chooseFontDialog.button.cancel" defaultMessage="Cancel" />
            </CalciteButton>
        );

    return !show ? null : mode === 'panel' ? (
        <CalcitePanel heading={title}>
            <div>
                {message !== undefined ? <div>{message}</div> : null}
                <CalciteBlock heading="" open={true}>
                    {fontForm}
                </CalciteBlock>
            </div>
            {secondaryButton}
            {primaryButton}
        </CalcitePanel>
    ) : (
        <ModalDialog
            title={
                title !== undefined ? (
                    title
                ) : (
                    <>
                        <FormattedMessage
                            id="chooseFontDialog.title"
                            defaultMessage="Choose font"
                        />
                    </>
                )
            }
            helpLink={`${appHelp.replace(/^(.*)\/$/, '$1')}/fonts/`}
            buttons={
                <>
                    {secondaryButton}
                    {primaryButton}
                </>
            }
            show={show}
            large={large}
            scroll={false}
            overflow="visible"
            onClose={onClose}
            {...others}
        >
            <div>
                {icon !== undefined ? (
                    <div>
                        <div
                            style={{
                                float: 'left',
                                marginRight: '10px',
                                marginBottom: 10 + 'px',
                                fontSize: '48px'
                            }}
                        >
                            {icon}
                        </div>
                        <div style={{ marginLeft: '68px' }}>
                            {message !== undefined ? <p>{message}</p> : null}
                            {fontForm}
                        </div>
                    </div>
                ) : (
                    <div className="form-horizontal">
                        {message !== undefined ? <p>{message}</p> : null}
                        {fontForm}
                    </div>
                )}
            </div>
        </ModalDialog>
    );
};

const buildBoxesList = (
    items: any[],
    selectedFamily: string,
    changeHandler: (e: any, f: string) => void
) => {
    if (items !== undefined) {
        const accordionItems = items.map((mi, index) => {
            return (
                <CalciteAccordionItem key={index} heading={mi.label}>
                    <CalciteList className="font-family-list">
                        {mi.families.map((family: string, fi: number) => {
                            return (
                                <CalciteListItem
                                    key={fi}
                                    onCalciteListItemSelect={(e) => changeHandler(e, family)}
                                >
                                    <span
                                        slot="content"
                                        style={{ fontFamily: family.split(':')[0] }}
                                    >
                                        {family.split(':')[0]}
                                    </span>
                                </CalciteListItem>
                            );
                        })}
                    </CalciteList>
                </CalciteAccordionItem>
            );
        });
        return (
            <CalciteAccordion appearance="transparent" scale="m">
                {accordionItems}
            </CalciteAccordion>
        );
    }
    return null;
};

type FontSet = {
    id: string;
    label: string;
    families: string[];
    fallback?: string;
};

export const STANDARD_FONTS: FontSet[] = [
    {
        id: 'sansSerif',
        label: 'Sans-Serif',
        families: [
            'Alegreya Sans',
            'Archivo',
            'Arimo',
            'Dosis',
            //'Droid Sans*',
            'Epilogue',
            'Inter',
            'Lato',
            'Lexend',
            'Manrope',
            'Montserrat',
            'Mulish',
            'Noto Sans',
            'Nunito',
            'Open Sans',
            'Oxygen',
            'Poppins',
            'Quattrocento Sans',
            'Quicksand',
            'Raleway',
            'PT Sans',
            'PT Sans Narrow',
            'Roboto',
            'Roboto Condensed',
            'Sora',
            'Source Sans Pro',
            'Work Sans'
        ],
        fallback: 'sans-serif'
    },
    {
        id: 'serif',
        label: 'Serif',
        families: [
            //'Andada',
            'Bitter',
            'Bree Serif',
            'Cantata One',
            //'Droid Serif',
            'Faustina',
            'Hepta Slab',
            //'Libre Baskerville',
            'Lora',
            'Merriweather',
            'Noto Serif',
            'PT Serif',
            'Quattrocento',
            'Roboto Slab',
            'Tinos'
        ],
        fallback: 'serif'
    },
    {
        id: 'display',
        label: 'Display',
        families: [
            'Alegreya Sans SC',
            'Changa One',
            'Coda Caption:800',
            'Comfortaa',
            'Gruppo',
            'Lalezar',
            //'Open Sans Condensed:300',
            'Overlock',
            'Paytone One',
            'Prosto One',
            'Syncopate'
        ],
        fallback: 'sans-serif'
    }
];

export const injectStandardFonts = (fonts?: FontSet[], cleanup = false, basePath = '/') => {
    const h = document.head,
        items = fonts !== undefined ? fonts : STANDARD_FONTS,
        existing: string[] = [];
    if (cleanup) h.querySelectorAll('.rb--font-family-link').forEach((e) => e.remove());
    else {
        h.querySelectorAll('.rb--font-family-link').forEach((e) => {
            const eid = e.getAttribute('data-family-key');
            if (eid !== undefined && eid !== null) existing.push(eid);
        });
    }
    let linkTag, ffKey;
    for (let ft of items) {
        for (let ff of ft.families) {
            ffKey = ff.replace(/[^0-9a-zA-Z]/g, '-').toLowerCase();
            if (existing.indexOf(ffKey) < 0) {
                if (ff.indexOf('*') === ff.length - 1) {
                    linkTag = document.createElement('link');
                    linkTag.setAttribute('rel', 'stylesheet');
                    linkTag.setAttribute('class', 'rb--font-family-link');
                    linkTag.setAttribute(
                        'href',
                        `https://fonts.googleapis.com/css?family=${encodeURIComponent(
                            ff.replace(/[*]/g, '')
                        )}&display=swap`
                    );
                    linkTag.setAttribute('data-family-key', ffKey);
                    h.appendChild(linkTag);
                } else {
                    linkTag = document.createElement('style');
                    linkTag.setAttribute('class', 'rb--font-family-link');
                    linkTag.setAttribute('data-family-key', ffKey);
                    const [fontFace, fontWeight = '400'] = ff.split(':'),
                        faceDef = `@font-face {
                        font-family: '${fontFace}';
                        src: url('${basePath}static/webfonts/fontsource/${fontFace
                            .toLowerCase()
                            .replace(
                                /[^0-9a-zA-Z-]/g,
                                '-'
                            )}-latin-${fontWeight}-normal.woff2') format('woff2'),
                            url('${basePath}static/webfonts/fontsource/${fontFace
                                .toLowerCase()
                                .replace(
                                    /[^0-9a-zA-Z-]/g,
                                    '-'
                                )}-latin-${fontWeight}-normal.woff') format('woff');
                        font-weight: normal;
                        font-style: normal;
                        font-display: swap;
                        unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
                    }
                    @font-face {
                        font-family: '${fontFace}';
                        src: url('${basePath}static/webfonts/fontsource/${fontFace
                            .toLowerCase()
                            .replace(
                                /[^0-9a-zA-Z-]/g,
                                '-'
                            )}-latin-ext-${fontWeight}-normal.woff2') format('woff2'),
                            url('${basePath}static/webfonts/fontsource/${fontFace
                                .toLowerCase()
                                .replace(
                                    /[^0-9a-zA-Z-]/g,
                                    '-'
                                )}-latin-ext-${fontWeight}-normal.woff') format('woff');
                        font-weight: normal;
                        font-style: normal;
                        font-display: swap;
                        unicode-range: U+0100-02AF, U+0304, U+0308, U+0329, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF;
                    }`;
                    linkTag.appendChild(document.createTextNode(faceDef));
                    h.appendChild(linkTag);
                }
            }
        }
    }
};

export const FontChooserPopover = ({
    referenceElement,
    fonts = STANDARD_FONTS,
    selected,
    onClose = () => {},
    onSelectionChange
}: {
    referenceElement: any;
    fonts?: FontSet[];
    selected?: string;
    onClose?: () => void;
    onSelectionChange: (e: any, family: string) => void;
}) => {
    const intl = useIntl();
    const listItems = buildBoxesList(fonts, selected ?? '', onSelectionChange);
    return (
        <CalcitePopover
            label="Fonts"
            referenceElement={referenceElement}
            onCalcitePopoverClose={(e) => {
                e.stopPropagation();
                onClose();
            }}
            overlayPositioning="fixed"
            placement="auto"
            scale="l"
            open={true}
            triggerDisabled={true}
        >
            <CalciteFlow>
                <CalciteFlowItem
                    heading={intl.formatMessage({
                        id: 'chooseFontDialog.dialog.families',
                        defaultMessage: 'Choose font family...'
                    })}
                    closable={true}
                    onCalciteFlowItemClose={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                >
                    <div className="items-collection big-popover-content">{listItems}</div>
                </CalciteFlowItem>
            </CalciteFlow>
        </CalcitePopover>
    );
};

export default ChooseFontDialog;
